import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Spinner, EmptyState } from "@/components/ui";
import clsx from "clsx";

const RARITY_META = {
  common:    { color: "#9ca3af", glow: "rgba(156,163,175,0.4)",  bg: "rgba(156,163,175,0.06)", border: "rgba(156,163,175,0.3)",  label: "Common"    },
  rare:      { color: "#3b82f6", glow: "rgba(59,130,246,0.5)",   bg: "rgba(59,130,246,0.07)",  border: "rgba(59,130,246,0.55)",  label: "Rare"      },
  epic:      { color: "#8b5cf6", glow: "rgba(139,92,246,0.5)",   bg: "rgba(139,92,246,0.08)",  border: "rgba(139,92,246,0.65)",  label: "Epic"      },
  legendary: { color: "#fbbf24", glow: "rgba(251,191,36,0.55)",  bg: "rgba(251,191,36,0.06)",  border: "rgba(251,191,36,0.75)",  label: "Legendary" },
};

/* ─── Radial progress ring ───────────────────────────────────────────────────── */
function ProgressRing({ pct, size = 80 }) {
  const r    = size / 2 - 5;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90 absolute inset-0">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(139,92,246,0.1)" strokeWidth="4"/>
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke="url(#prog-grad)" strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ}`}
          style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.22,1,0.36,1)", filter: "drop-shadow(0 0 4px rgba(139,92,246,0.7))" }}
        />
        <defs>
          <linearGradient id="prog-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#8b5cf6"/>
            <stop offset="100%" stopColor="#06b6d4"/>
          </linearGradient>
        </defs>
      </svg>
      <div className="text-center z-10">
        <p className="text-sm font-black text-white">{pct}%</p>
      </div>
    </div>
  );
}

/* ─── Badge card (earned) ────────────────────────────────────────────────────── */
function BadgeCard({ a, earnedAt }) {
  const meta = RARITY_META[a.rarity] || RARITY_META.common;
  const isLegendary = a.rarity === "legendary";

  return (
    <div
      className={clsx(
        "relative flex flex-col items-center p-4 rounded-2xl border-2 text-center overflow-hidden",
        "transition-all duration-300 hover:-translate-y-0.5 hover:scale-[1.02] group",
      )}
      style={{
        borderColor: meta.border,
        background:  meta.bg,
        boxShadow:   `0 4px 16px ${meta.glow.replace("0.5","0.2")}, 0 0 0 1px rgba(255,255,255,0.03) inset`,
        animation:   isLegendary ? "legendary-pulse 2.5s ease-in-out infinite" : undefined,
      }}
    >
      {/* Top shimmer line */}
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${meta.color}50, transparent)` }} />

      {/* Holographic hover effect */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none holographic" style={{ borderRadius: "14px" }} />

      {/* Rarity badge */}
      <div
        className="absolute top-2 right-2 text-[8px] font-black px-1.5 py-0.5 rounded-md"
        style={{ color: meta.color, background: `${meta.color}15`, border: `1px solid ${meta.color}30` }}
      >
        {meta.label.toUpperCase()}
      </div>

      {/* Icon with glow */}
      <div
        className="text-4xl mb-2 transition-transform duration-300 group-hover:scale-110"
        style={{ filter: `drop-shadow(0 0 16px ${meta.glow})` }}
      >
        {a.icon}
      </div>

      <p className="font-black text-xs leading-tight mb-1">{a.title}</p>
      <p className="text-[10px] text-gray-500 leading-relaxed mb-2">{a.description}</p>

      <div className="flex justify-center gap-2 mb-2">
        {a.xpReward > 0 && (
          <span className="text-[10px] font-black text-violet-400 bg-violet-500/10 px-1.5 py-0.5 rounded-md">+{a.xpReward}XP</span>
        )}
        {a.coinsReward > 0 && (
          <span className="text-[10px] font-black text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded-md">+{a.coinsReward}🪙</span>
        )}
      </div>

      {earnedAt && (
        <p className="text-[9px] text-gray-600 mt-auto">{new Date(earnedAt).toLocaleDateString("de-DE")}</p>
      )}
    </div>
  );
}

/* ─── Locked badge card ──────────────────────────────────────────────────────── */
function LockedBadge({ a }) {
  return (
    <div
      className="relative flex flex-col items-center p-4 rounded-2xl border border-nexus-border text-center overflow-hidden opacity-45 group transition-all duration-200 hover:opacity-60"
      style={{ background: "rgba(13,13,43,0.5)" }}
    >
      <div className="text-3xl mb-2 grayscale">🔒</div>
      <p className="font-black text-xs leading-tight mb-1 text-gray-400">{a.title}</p>
      <p className="text-[10px] text-gray-600 leading-relaxed">{a.description}</p>
      <div className="flex justify-center gap-2 mt-2">
        {a.xpReward > 0 && <span className="text-[10px] text-gray-600">+{a.xpReward}XP</span>}
        {a.coinsReward > 0 && <span className="text-[10px] text-gray-600">+{a.coinsReward}🪙</span>}
      </div>
    </div>
  );
}

export default function Achievements() {
  const { data: allData, isLoading: loadingAll } = useQuery({
    queryKey: ["achievements"],
    queryFn:  () => api.get("/achievements").then((r) => r.data),
  });

  const { data: myData, isLoading: loadingMy } = useQuery({
    queryKey: ["my-achievements"],
    queryFn:  () => api.get("/achievements/mine").then((r) => r.data),
  });

  if (loadingAll || loadingMy) return (
    <div className="flex justify-center pt-20">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-2 border-t-yellow-500 border-r-violet-500 border-b-transparent border-l-transparent animate-spin" />
      </div>
    </div>
  );

  const all       = allData?.achievements || [];
  const earned    = myData?.achievements  || [];
  const earnedMap = new Map(earned.map((a) => [a.achievementId, a]));
  const done      = earned.length;
  const pct       = all.length > 0 ? Math.round((done / all.length) * 100) : 0;
  const locked    = all.filter((a) => !earnedMap.has(a.id));

  return (
    <div className="space-y-5 animate-fade-in">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="text-center py-3 relative">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.06) 0%, transparent 70%)" }} />
        <div
          className="text-5xl mb-3 inline-block animate-float"
          style={{ filter: "drop-shadow(0 0 20px rgba(251,191,36,0.7))" }}
        >
          🏅
        </div>
        <h1 className="text-2xl font-black">Achievements</h1>
        <p className="text-xs text-gray-500 mt-1">{done}/{all.length} freigeschaltet</p>
      </div>

      {/* ── Overall progress ───────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-4 p-4 rounded-2xl border border-nexus-border relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, rgba(19,19,58,0.9) 0%, rgba(13,13,43,0.85) 100%)" }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.05) 0%, transparent 70%)" }} />
        <ProgressRing pct={pct} size={72} />
        <div>
          <p className="font-black text-sm">Gesamtfortschritt</p>
          <p className="text-xs text-gray-500 mt-0.5">{done} von {all.length} Abzeichen</p>
          <div className="flex gap-1.5 mt-2">
            {[...new Set(earned.map((ea) => ea.achievement?.rarity))].filter(Boolean).map((r) => {
              const m = RARITY_META[r];
              return m ? (
                <div key={r} className="w-2 h-2 rounded-full" style={{ background: m.color, boxShadow: `0 0 4px ${m.color}` }} />
              ) : null;
            })}
          </div>
        </div>
      </div>

      {/* ── Earned achievements ────────────────────────────────────────────── */}
      {earned.length > 0 && (
        <section>
          <h3 className="font-black text-sm mb-3 flex items-center gap-2">
            <span className="text-green-400">✅</span>
            <span>Freigeschaltet ({done})</span>
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {earned.map((ea) => (
              <BadgeCard key={ea.id} a={ea.achievement} earnedAt={ea.awardedAt} />
            ))}
          </div>
        </section>
      )}

      {/* ── Locked achievements ────────────────────────────────────────────── */}
      <section>
        <h3 className="font-black text-sm mb-3 flex items-center gap-2">
          <span className="text-gray-500">🔒</span>
          <span className="text-gray-400">Gesperrt ({locked.length})</span>
        </h3>

        {locked.length === 0 ? (
          <div
            className="p-8 rounded-2xl border border-yellow-500/30 text-center relative overflow-hidden"
            style={{ background: "rgba(251,191,36,0.04)" }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(251,191,36,0.07) 0%, transparent 70%)" }} />
            <div className="text-5xl mb-3" style={{ filter: "drop-shadow(0 0 16px rgba(251,191,36,0.6))" }}>🎊</div>
            <p className="font-black text-lg text-gradient-gold">Alle freigeschaltet!</p>
            <p className="text-xs text-gray-500 mt-1">Du bist eine absolute Legende.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {locked.map((a) => <LockedBadge key={a.id} a={a} />)}
          </div>
        )}
      </section>

      <div className="pb-2" />
    </div>
  );
}
